import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import { GlobalContext } from '../../../context/GlobalContext';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import { formatCurrency, formatPercentage, calculatePercentageChange } from '../shared/utils';
import { exportToPDF, exportToExcel, exportToCSV, prepareEmailData } from '../shared/exportUtils';
import haxios from '../../../lib/haxios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { Building2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { modals } from '@mantine/modals';
import { successToast, errorToast } from '../../../lib/helpers';

const BranchReport = () => {
  console.log('BranchReport - Component is rendering');
  const { uinfo } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  console.log('BranchReport - uinfo:', uinfo);
  const [loading, setLoading] = useState(false);
  const [allBranchData, setAllBranchData] = useState([]); // Monthly totals for comparison
  const [todayBranchData, setTodayBranchData] = useState([]); // Today's data for leaderboard
  const [branchSparklines, setBranchSparklines] = useState({}); // code -> { points:number[], dates:string[] }
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [expandedBranches, setExpandedBranches] = useState(new Set()); // Track expanded branches

  // Get branches list - exact same as working BranchPerformance component
  const { data: branches, loading: branchesLoading } = useApi(
    '/salesassist/cplusassist/branches',
    { initial: [] }
  );
  
  // Fetch staff data for all branches to calculate costs
  const { data: allStaffData = [], isLoading: staffLoading } = useApi({
    url: '/salesassistnew/ustafferators',
    initial: []
  });
  
  // Fetch branch settings to get margin targets and sales targets
  const { data: branchSettings = [], isLoading: branchSettingsLoading } = useApi({
    url: '/salesassist/cplusassist/branches',
    initial: []
  });
  
  console.log('BranchReport - branches:', branches);
  console.log('BranchReport - branchesLoading:', branchesLoading);

  // Read branch filter from URL query parameter on mount
  useEffect(() => {
    const branchParam = searchParams.get('branch');
    if (branchParam) {
      const decodedBranch = decodeURIComponent(branchParam);
      setSelectedBranchFilter(decodedBranch);
      console.log('BranchReport - Branch filter from URL:', decodedBranch);
    }
  }, [searchParams]);

  // Fetch today's data for leaderboard (branchPerformance endpoint)
  useEffect(() => {
    const fetchTodayData = async () => {
      try {
        console.log('BranchReport - Fetching today\'s data for leaderboard');
        
        // Use hardcoded branch codes (AVDL, AVDL2, Finchley)
        const branchCodes = ['AVDL', 'AVDL2', 'Finchley'];
        console.log('BranchReport - Branch codes:', branchCodes);
        
        const todayPromises = branchCodes.map(async (branchCode) => {
          try {
            const response = await haxios.get(`/visiondashboard/branchPerformance/${branchCode}`);
            console.log(`BranchReport - Today's data for ${branchCode}:`, response.data);
            return {
              branch: branchCode,
              ...response.data
            };
          } catch (error) {
            console.error(`BranchReport - Failed to fetch today's data for ${branchCode}:`, error);
            return null;
          }
        });

        const todayResults = await Promise.all(todayPromises);
        const validTodayResults = todayResults.filter(result => result !== null);
        console.log('BranchReport - Today\'s data:', validTodayResults);
        setTodayBranchData(validTodayResults);
      } catch (error) {
        console.error('BranchReport - Failed to fetch today\'s data:', error);
      }
    };

    fetchTodayData();
  }, []);

  // Fetch monthly sales data for ALL branches (for comparison table)
  useEffect(() => {
    const fetchMonthlyData = async () => {
      try {
        setLoading(true);
        console.log('BranchReport - Fetching monthly data for comparison table');
        
        // Get current month/year for monthly totals
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        // Use hardcoded branch codes (AVDL, AVDL2, Finchley)
        const branchCodes = ['AVDL', 'AVDL2', 'Finchley'];
        console.log('BranchReport - Branch codes for monthly data:', branchCodes);
        
        // Fetch monthly sales data for each branch (same as /sales page)
        const branchDataPromises = branchCodes.map(async (branchCode) => {
          try {
            console.log(`BranchReport - Fetching monthly sales for ${branchCode}`);
            
            // Fetch monthly sales data (same endpoint as /sales page)
            // Note: haxios returns response.data directly, not the full response object
            const salesData = await haxios.get(
              `/visiondashboard/salesassist/getSales/${currentYear}/${currentMonth}/${branchCode}`
            );
            
            console.log(`BranchReport - Monthly response for ${branchCode}:`, salesData);
            
            // Calculate totals from monthly data (EXACT same logic as InvoiceSummary)
            let totalSales = 0;
            let totalCost = 0;
            let totalMargin = 0;
            let totalCredits = 0;
            let invoiceCount = 0;
            
            // haxios already returns the data array directly
            const dataArray = Array.isArray(salesData) ? salesData : [];
            
            if (dataArray.length > 0) {
              // Sum all transactions - EXACT same as InvoiceSummary
              // This sums ALL days in the month for this branch
              dataArray.forEach(curr => {
                totalSales += curr.sales || 0;
                totalCost += curr.cost || 0;
                totalCredits += curr.credits || 0;
                invoiceCount += curr.invoices || 0;
              });
              
              // ALWAYS calculate margin as Sales - Cost for accuracy
              // This gives us the true cumulative monthly margin
              totalMargin = totalSales - totalCost;
            }
            
            // The API's curr.sales is Overall Sales (higher value before credits)
            // Net Sales = Overall Sales + Credits (credits are negative, so this reduces the total)
            const overallSales = totalSales; // What API returns is Overall Sales
            const netSales = totalSales + totalCredits; // Net Sales is lower (after credits)
            
            // Calculate margin percent using Overall Sales (the value margin is calculated on)
            // Margin = Overall Sales - Cost, so margin % = Margin / Overall Sales
            // This is the CUMULATIVE monthly margin
            const marginPercent = overallSales > 0 ? (totalMargin / overallSales) * 100 : 0;
            
            return {
              branch: branchCode,
              totalSales: overallSales, // Overall Sales (higher value, from API)
              netSales: netSales,       // Net Sales (lower value, after credits)
              totalCost,                 // Total Cost (for margin calculation)
              totalMargin,
              totalCredits,
              marginPercent,
              invoiceCount
            };
          } catch (error) {
            console.error(`BranchReport - Failed to fetch monthly data for ${branchCode}:`, error);
            return null;
          }
        });

        const allBranchResults = await Promise.all(branchDataPromises);
        const validResults = allBranchResults.filter(result => result !== null);
        console.log('BranchReport - Monthly branch results:', validResults);
        setAllBranchData(validResults);
      } catch (error) {
        console.error('BranchReport - Failed to fetch monthly branch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyData();
  }, []);

  // Helper: format date YYYYMMDD
  const fmtYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  };

  // Fetch actual daily sales data for last 7 days
  useEffect(() => {
    const fetchDailySales = async () => {
      try {
        const sparkData = {};
        
        // Calculate date range (last 7 days)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 6); // 7 days including today
        
        const startDateStr = fmtYMD(startDate);
        const endDateStr = fmtYMD(endDate);
        
        // Use hardcoded branch codes (AVDL, AVDL2, Finchley)
        const branchCodes = ['AVDL', 'AVDL2', 'Finchley'];
        
        // Fetch daily sales for each branch
        const dailyPromises = branchCodes.map(async (branchCode) => {
          try {
            // haxios returns response.data directly
            const dailyData = await haxios.get(
              `/visiondashboard/salesassist/getDailySales/${startDateStr}/${endDateStr}/${branchCode}`
            );
            return { branchCode, data: dailyData };
          } catch (error) {
            console.error(`Failed to fetch daily sales for ${branchCode}:`, error);
            return { branchCode, data: null };
          }
        });
        
        const dailyResults = await Promise.all(dailyPromises);
        
        // Process daily data into sparkline format
        dailyResults.forEach(({ branchCode, data }) => {
          if (!data || !data.daily) {
            sparkData[branchCode] = { points: [], dates: [] };
            return;
          }
          
          const points = [];
          const dates = [];
          
          // Sort daily data by date
          const sortedDaily = (data.daily || []).sort((a, b) => {
            return new Date(a.date) - new Date(b.date);
          });
          
          sortedDaily.forEach(day => {
            const dayDate = new Date(day.date);
            const dayStr = String(dayDate.getDate()).padStart(2, '0');
            const month = String(dayDate.getMonth() + 1).padStart(2, '0');
            const year = String(dayDate.getFullYear()).slice(-2);
            dates.push(`${dayStr}/${month}/${year}`);
            
            // Use invoiceValue (sales) for the sparkline
            points.push(parseFloat(day.invoiceValue || 0));
          });
          
          sparkData[branchCode] = { points, dates };
        });
        
        console.log('BranchReport - Fetched daily sales:', sparkData);
        setBranchSparklines(sparkData);
      } catch (e) {
        console.error('Failed to fetch daily sales', e);
      }
    };

    fetchDailySales();
  }, []);

  // Process monthly data for comparison table and branch details
  const processedData = React.useMemo(() => {
    if (!allBranchData || !Array.isArray(allBranchData) || allBranchData.length === 0) {
      return { branches: [] };
    }
    
    // Filter branches if selectedBranchFilter is set
    let filteredBranchData = allBranchData;
    if (selectedBranchFilter) {
      filteredBranchData = allBranchData.filter(branchData => {
        const branchName = branchData.branch || '';
        return branchName.toUpperCase() === selectedBranchFilter.toUpperCase();
      });
    }
    
    // Calculate staff costs per branch
    const staffCostsByBranch = {};
    if (allStaffData && Array.isArray(allStaffData)) {
      allStaffData.forEach(staff => {
        const branchCode = staff.Branch;
        if (branchCode) {
          if (!staffCostsByBranch[branchCode]) {
            staffCostsByBranch[branchCode] = {
              totalCost: 0,
              staffCount: 0
            };
          }
          staffCostsByBranch[branchCode].totalCost += (parseFloat(staff.MonthlyCostPerHead) || 0);
          staffCostsByBranch[branchCode].staffCount++;
        }
      });
    }
    
    // Build per-branch KPI sets from monthly data
    const branches = filteredBranchData.map(branchData => {
      const branchCode = branchData.branch;
      const staffCosts = staffCostsByBranch[branchCode] || { totalCost: 0, staffCount: 0 };
      
      // Get margin target from branch settings
      const branchSetting = branchSettings?.find(b => b.MamCode === branchCode || b.Branch === branchCode);
      const marginTarget = branchSetting?.MarginTarget ? parseFloat(branchSetting.MarginTarget) : 0;
      const monthlySalesTarget = branchSetting?.MonthlySalesTarget ? parseFloat(branchSetting.MonthlySalesTarget) : null;
      
      // Calculate required turnover
      let requiredTurnover = 0;
      if (monthlySalesTarget) {
        requiredTurnover = monthlySalesTarget;
      } else if (marginTarget > 0) {
        requiredTurnover = staffCosts.totalCost / (1 - (marginTarget / 100));
      } else {
        requiredTurnover = staffCosts.totalCost; // Break even at minimum
      }
      
      // Calculate profit/loss (using monthly sales data)
      const currentSales = parseFloat(branchData.totalSales || 0); // This is already monthly data
      const profitLoss = currentSales - staffCosts.totalCost;
      
      return {
        code: branchCode,
        marginPercent: branchData.marginPercent || 0, // Use calculated margin percent, not target
        invoiceCount: branchData.invoiceCount || 0, // Add invoice count
        DailySalesTarget: branchSetting?.DailySalesTarget || null, // Added
        MonthlySalesTarget: branchSetting?.MonthlySalesTarget || null, // Added
        kpis: [
          {
            key: `sales-${branchCode}`,
            label: 'Overall Sales',
            value: currentSales,
            type: 'sales',
            format: 'currency'
          },
          {
            key: `net-${branchCode}`,
            label: 'Total Cost',
            value: parseFloat(branchData.netSales || 0),
            type: 'sales',
            format: 'currency'
          },
          {
            key: `cost-${branchCode}`,
            label: 'Total Cost',
            value: parseFloat(branchData.totalCost || 0),
            type: 'cost',
            format: 'currency'
          },
          {
            key: `margin-${branchCode}`,
            label: 'Margin',
            value: parseFloat(branchData.totalMargin || 0),
            type: 'margin',
            format: 'currency'
          },
          {
            key: `credits-${branchCode}`,
            label: 'Credits',
            value: parseFloat(branchData.totalCredits || 0),
            type: 'credits',
            format: 'currency'
          },
          {
            key: `staff-costs-${branchCode}`,
            label: 'Staff Costs (Monthly)',
            value: staffCosts.totalCost,
            type: 'cost',
            format: 'currency',
            subtitle: `${staffCosts.staffCount} staff members`
          },
          {
            key: `required-turnover-${branchCode}`,
            label: 'Required Turnover',
            value: requiredTurnover,
            type: 'sales',
            format: 'currency',
            subtitle: 'To break even'
          },
          {
            key: `profit-loss-${branchCode}`,
            label: 'Profit/Loss',
            value: profitLoss,
            type: profitLoss >= 0 ? 'margin' : 'credits',
            format: 'currency',
            subtitle: profitLoss >= 0 ? 'In profit' : 'At loss',
            color: profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
          }
        ]
      };
    });

    return { branches };
  }, [allBranchData, selectedBranchFilter, allStaffData, branchSettings]);

  // Process today's data for leaderboard
  const processedTodayData = React.useMemo(() => {
    if (!todayBranchData || !Array.isArray(todayBranchData) || todayBranchData.length === 0) {
      return { branches: [] };
    }
    
    // Filter if needed
    let filtered = todayBranchData;
    if (selectedBranchFilter) {
      filtered = todayBranchData.filter(branchData => {
        const branchName = branchData.branch || '';
        return branchName.toUpperCase() === selectedBranchFilter.toUpperCase();
      });
    }
    
    // Build per-branch KPI sets from today's data
    const branches = filtered.map(branchData => {
      return {
        code: branchData.branch,
        marginPercent: parseFloat(branchData.marginPercent || 0),
        kpis: [
          {
            key: `sales-${branchData.branch}`,
            label: 'Overall Sales',
            value: parseFloat(branchData.totalSales || 0),
            type: 'sales',
            format: 'currency'
          },
          {
            key: `net-${branchData.branch}`,
            label: 'Total Cost',
            value: parseFloat(branchData.netSales || 0),
            type: 'sales',
            format: 'currency'
          },
          {
            key: `margin-${branchData.branch}`,
            label: 'Margin',
            value: parseFloat(branchData.totalMargin || 0),
            type: 'margin',
            format: 'currency'
          },
          {
            key: `credits-${branchData.branch}`,
            label: 'Credits',
            value: parseFloat(branchData.totalCredits || 0),
            type: 'credits',
            format: 'currency'
          }
        ]
      };
    });

    return { branches };
  }, [todayBranchData, selectedBranchFilter]);


  // Prepare export data
  const prepareExportData = () => {
    // Flatten branch data for table export
    const tableData = processedData.branches.map(branch => {
      const salesKpi = branch.kpis?.find(k => k.key.startsWith('sales-'));
      const costKpi = branch.kpis?.find(k => k.key.startsWith('cost-'));
      const marginKpi = branch.kpis?.find(k => k.key.startsWith('margin-'));
      const creditsKpi = branch.kpis?.find(k => k.key.startsWith('credits-'));
      
      return {
        branch: branch.code,
        overallSales: salesKpi?.value || 0,
        netSales: costKpi?.value || 0, // Using costKpi for Total Cost
        margin: marginKpi?.value || 0,
        marginPercent: branch.marginPercent || 0,
        credits: creditsKpi?.value || 0
      };
    });

    // Combine all KPIs from all branches
    const allKpis = processedData.branches.flatMap(branch => 
      branch.kpis?.map(kpi => ({
        ...kpi,
        label: `${branch.code} - ${kpi.label}`
      })) || []
    );

    return {
      tableData,
      kpis: allKpis,
      columns: [
        { key: 'branch', label: 'Branch', format: 'text' },
        { key: 'overallSales', label: 'Overall Sales', format: 'currency' },
        { key: 'netSales', label: 'Total Cost', format: 'currency' },
        { key: 'margin', label: 'Margin', format: 'currency' },
        { key: 'marginPercent', label: 'Margin %', format: 'percentage' },
        { key: 'credits', label: 'Credits', format: 'currency' }
      ]
    };
  };

  // Export handlers
  const handleExportPDF = () => {
    try {
      const exportData = prepareExportData();
      const filename = `branch-performance-${selectedBranchFilter || 'all'}-${new Date().toISOString().split('T')[0]}.pdf`;
      
      exportToPDF({
        title: 'Branch Performance Report',
        subtitle: 'Comprehensive analysis of sales performance by branch',
        filename,
        columns: exportData.columns,
        tableData: exportData.tableData,
        kpis: exportData.kpis,
        branchName: selectedBranchFilter || null
      });
      
      successToast('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      errorToast('Failed to export PDF. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const exportData = prepareExportData();
      const filename = `branch-performance-${selectedBranchFilter || 'all'}-${new Date().toISOString().split('T')[0]}.xlsx`;
      
      exportToExcel(exportData.tableData, {
        title: 'Branch Performance Report',
        filename,
        columns: exportData.columns,
        tableData: exportData.tableData,
        kpis: exportData.kpis,
        branchName: selectedBranchFilter || null
      });
      
      successToast('Excel file exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      errorToast('Failed to export Excel file. Please try again.');
    }
  };

  const handleEmailReport = () => {
    modals.open({
      title: 'Email Branch Performance Report',
      centered: true,
      children: (
        <EmailReportModal
          onSend={async (email) => {
            try {
              setLoading(true);
              const exportData = prepareExportData();
              const emailData = prepareEmailData(exportData.tableData, {
                title: 'Branch Performance Report',
                recipientEmail: email,
                columns: exportData.columns,
                tableData: exportData.tableData,
                kpis: exportData.kpis,
                branchName: selectedBranchFilter || null
              });

              // Call email API endpoint
              await haxios.post('/visiondashboard/reports/email', emailData);
              
              successToast(`Report sent to ${email} successfully`);
              modals.closeAll();
            } catch (error) {
              console.error('Email failed:', error);
              errorToast('Failed to send email. Please try again.');
            } finally {
              setLoading(false);
            }
          }}
        />
      )
    });
  };

  const handleRowClick = (row) => {
    // Navigate to detailed branch view or show modal
    console.log('Branch clicked:', row);
  };

  const isLoading = loading || branchesLoading;

  console.log('BranchReport - About to render:');
  console.log('BranchReport - isLoading:', isLoading);
  console.log('BranchReport - processedData.kpis:', processedData.kpis);
  console.log('BranchReport - processedData.tableData:', processedData.tableData);

  const handleClearBranchFilter = () => {
    setSelectedBranchFilter(null);
    setSearchParams({});
    navigate('/reports/branch');
  };

  const toggleBranch = (branchCode) => {
    setExpandedBranches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(branchCode)) {
        newSet.delete(branchCode);
      } else {
        newSet.add(branchCode);
      }
      return newSet;
    });
  };

  return (
    <ReportLayout
      title="Branch Performance Report"
      subtitle="Comprehensive analysis of sales performance by branch"
      icon={Building2}
      iconColor="blue"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onEmailReport={handleEmailReport}
      rightActions={
        selectedBranchFilter ? (
          <button
            onClick={handleClearBranchFilter}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Branch Filter
          </button>
        ) : null
      }
      loading={isLoading}
    >
      {selectedBranchFilter && (
        <div className="mb-4 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-indigo-800">
              Showing data for branch: <strong>{selectedBranchFilter}</strong>
            </span>
            <button
              onClick={handleClearBranchFilter}
              className="text-sm text-indigo-600 hover:text-indigo-800 underline"
            >
              Show all branches
            </button>
          </div>
        </div>
      )}
      {/* Leaderboard */}
      <div className="mb-6 px-1">
        <div className="flex items-center justify-between mb-3 ml-3">
          <h2 className="text-lg font-semibold text-gray-900">Branch Leaderboard</h2>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Today's Performance</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Combined multi-branch 7-day trend */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6 shadow-sm ring-1 ring-black/5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium text-gray-800">7-day Sales Trend</div>
              <div className="flex items-center space-x-4 text-xs">
                {processedTodayData.branches?.map((branch, idx) => {
                  const colors = ['bg-blue-600', 'bg-emerald-600', 'bg-orange-600'];
                  return (
                    <div key={branch.code} className="flex items-center space-x-1">
                      <span className={`w-2 h-2 rounded-full ${colors[idx] || 'bg-gray-600'}`}></span>
                      <span>{branch.code}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {(() => {
              // Use actual branch codes from today's data, but match the case from sparkline data
              const actualCodes = processedTodayData.branches?.map(b => b.code) || [];
              const sparklineKeys = Object.keys(branchSparklines);
              
              // Match actual codes to sparkline keys (handle case differences)
              const matchedCodes = actualCodes.map(code => {
                const upperCode = code.toUpperCase();
                const foundKey = sparklineKeys.find(key => key.toUpperCase() === upperCode);
                return foundKey || code;
              });
              
              const series = matchedCodes.map(code => branchSparklines[code] || { points: [], dates: [] });
              const longest = series.reduce((acc, s) => (s.points.length > acc.points.length ? s : acc), { points: [], dates: [] });
              const dates = longest.dates.length ? longest.dates : Array.from({ length: 7 }, (_, i) => `${i}`);

              const norm = series.map(s => {
                if (s.points.length === dates.length) return s.points;
                if (s.points.length === 0) return new Array(dates.length).fill(0);
                const copy = s.points.slice(-dates.length);
                while (copy.length < dates.length) copy.unshift(copy[0] || 0);
                return copy;
              });

              // Create dynamic chart data using matched branch codes
              const chartData = dates.map((d, i) => {
                const dataPoint = { date: d };
                matchedCodes.forEach((code, idx) => {
                  dataPoint[code] = norm[idx]?.[i] || 0;
                });
                return dataPoint;
              });

              return (
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#6b7280" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(l) => `Date: ${l}`} />
                      <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
                      {matchedCodes.map((code, idx) => {
                        const colors = ['#2563eb', '#059669', '#ea580c'];
                        return (
                          <Line 
                            key={code}
                            type="monotone" 
                            dataKey={code} 
                            stroke={colors[idx] || '#6b7280'} 
                            strokeWidth={2} 
                            dot={false} 
                            name={code} 
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </div>

          {/* Top-3 cards (today's performance) */}
          <div className="grid grid-cols-1 gap-4">
            {processedTodayData.branches
              .slice()
              .sort((a, b) => {
                const aSales = a.kpis?.find(k => k.key.startsWith('sales-'))?.value || 0;
                const bSales = b.kpis?.find(k => k.key.startsWith('sales-'))?.value || 0;
                return bSales - aSales;
              })
              .slice(0, 3)
              .map((b, idx) => {
                const salesKpi = b.kpis?.find(k => k.key.startsWith('sales-'));
                const salesVal = salesKpi?.value || 0;
                const marginPct = b.marginPercent || 0;
                const medal = idx === 0 ? 'bg-yellow-100 text-yellow-800' : idx === 1 ? 'bg-gray-100 text-gray-800' : 'bg-amber-100 text-amber-800';
                return (
                  <a 
                    key={b.code} 
                    href={`#branch-${b.code}`} 
                    className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow ring-1 ring-black/5"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedBranchFilter(b.code);
                      setSearchParams({ branch: b.code });
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${medal}`}>#{idx + 1}</div>
                      <div className="text-sm text-gray-500">{b.code}</div>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <div className="text-xl font-bold text-gray-900">{formatCurrency(salesVal)}</div>
                      <div className="text-xs text-gray-600">Today</div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">Margin {marginPct.toFixed(1)}%</div>
                  </a>
                );
              })}
          </div>
        </div>
      </div>

      {/* Branch Performance Dashboard */}
      <div className="space-y-6">
        {/* Summary Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Performance Summary</h2>
          
          {/* Total Stats Row - 8 KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
            {(() => {
              // Sum Total Sales across all branches
              const totalSales = processedData.branches.reduce((sum, b) => {
                const salesKpi = b.kpis?.find(k => k.key.startsWith('sales-'));
                return sum + (salesKpi?.value || 0);
              }, 0);
              
              // Sum Total Cost across all branches
              const totalCost = processedData.branches.reduce((sum, b) => {
                const costKpi = b.kpis?.find(k => k.key.startsWith('cost-'));
                return sum + (costKpi?.value || 0);
              }, 0);
              
              // Calculate Total Margin as Total Sales - Total Cost (same as /sales page)
              const totalMargin = totalSales - totalCost;
              
              const totalCredits = processedData.branches.reduce((sum, b) => {
                const creditsKpi = b.kpis?.find(k => k.key.startsWith('credits-'));
                return sum + (creditsKpi?.value || 0);
              }, 0);
              
              // Calculate average margin percent
              const avgMarginPercent = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;
              
              // Calculate overall profitability metrics
              const totalStaffCosts = processedData.branches.reduce((sum, b) => {
                const staffCostsKpi = b.kpis?.find(k => k.key.startsWith('staff-costs-'));
                return sum + (staffCostsKpi?.value || 0);
              }, 0);
              
              const totalRequiredTurnover = processedData.branches.reduce((sum, b) => {
                const requiredTurnoverKpi = b.kpis?.find(k => k.key.startsWith('required-turnover-'));
                return sum + (requiredTurnoverKpi?.value || 0);
              }, 0);
              
              const totalProfitLoss = processedData.branches.reduce((sum, b) => {
                const profitLossKpi = b.kpis?.find(k => k.key.startsWith('profit-loss-'));
                return sum + (profitLossKpi?.value || 0);
              }, 0);
              
              const totalStaffCount = allStaffData.length;
              const avgCostPerStaff = totalStaffCount > 0 ? totalStaffCosts / totalStaffCount : 0;
              
              return (
                <>
                  {/* Existing 4 Sales Metrics */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-medium text-blue-700 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-blue-900">{formatCurrency(totalSales)}</div>
                    <div className="text-xs text-blue-600 mt-1">{processedData.branches.length} branches</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="text-sm font-medium text-orange-700 mb-1">Total Cost</div>
                    <div className="text-2xl font-bold text-orange-900">{formatCurrency(totalCost)}</div>
                    <div className="text-xs text-orange-600 mt-1">All branches</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-700 mb-1">Total Margin</div>
                    <div className="text-2xl font-bold text-green-900">{formatCurrency(totalMargin)}</div>
                    <div className="text-xs text-green-600 mt-1">{formatPercentage(avgMarginPercent)} avg</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm font-medium text-purple-700 mb-1">Avg Margin %</div>
                    <div className="text-2xl font-bold text-purple-900">{formatPercentage(avgMarginPercent)}</div>
                    <div className="text-xs text-purple-600 mt-1">Across all branches</div>
                  </div>
                  
                  {/* New 4 Profitability Metrics */}
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-700 mb-1">Total Staff Costs</div>
                    <div className="text-2xl font-bold text-red-900">{formatCurrency(totalStaffCosts)}</div>
                    <div className="text-xs text-red-600 mt-1">{totalStaffCount} staff members</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="text-sm font-medium text-orange-700 mb-1">Required Turnover</div>
                    <div className="text-2xl font-bold text-orange-900">{formatCurrency(totalRequiredTurnover)}</div>
                    <div className="text-xs text-orange-600 mt-1">To break even</div>
                  </div>
                  
                  <div className={`rounded-lg p-4 border ${
                    totalProfitLoss >= 0 
                      ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' 
                      : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                  }`}>
                    <div className={`text-sm font-medium mb-1 ${
                      totalProfitLoss >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      Profit/Loss
                    </div>
                    <div className={`text-2xl font-bold ${
                      totalProfitLoss >= 0 ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {formatCurrency(totalProfitLoss)}
                    </div>
                    <div className={`text-xs mt-1 ${
                      totalProfitLoss >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {totalProfitLoss >= 0 ? 'In profit' : 'At loss'}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                    <div className="text-sm font-medium text-indigo-700 mb-1">Avg Cost Per Staff</div>
                    <div className="text-2xl font-bold text-indigo-900">{formatCurrency(avgCostPerStaff)}</div>
                    <div className="text-xs text-indigo-600 mt-1">Monthly average</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Branch Comparison Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Branch Comparison</h2>
            <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded border border-blue-200">
              Current Month Totals
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Branch</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Overall Sales</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Total Cost</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Margin</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Margin %</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Break Even</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Break Even Figures</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">% of Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Calculate total sales across all branches for percentage calculation
                  // Use Overall Sales for the total
                  const totalSalesAll = processedData.branches.reduce((sum, b) => {
                    const salesKpi = b.kpis?.find(k => k.key.startsWith('sales-'));
                    return sum + (salesKpi?.value || 0);
                  }, 0);
                  
                  return processedData.branches
                    .slice()
                    .sort((a, b) => {
                      // Sort by Overall Sales
                      const aSales = a.kpis?.find(k => k.key.startsWith('sales-'))?.value || 0;
                      const bSales = b.kpis?.find(k => k.key.startsWith('sales-'))?.value || 0;
                      return bSales - aSales;
                    })
                    .map((branch, idx) => {
                      const salesKpi = branch.kpis?.find(k => k.key.startsWith('sales-'));
                      const costKpi = branch.kpis?.find(k => k.key.startsWith('cost-'));
                      const marginKpi = branch.kpis?.find(k => k.key.startsWith('margin-'));
                      const requiredTurnoverKpi = branch.kpis?.find(k => k.key.startsWith('required-turnover-'));
                      
                      // Calculate percentage of total sales (using Overall Sales)
                      const branchOverallSales = salesKpi?.value || 0;
                      const branchTotalCost = costKpi?.value || 0;
                      const salesPercentage = totalSalesAll > 0 ? (branchOverallSales / totalSalesAll) * 100 : 0;
                      
                      // Calculate break even percentage
                      const requiredTurnover = requiredTurnoverKpi?.value || 0;
                      const breakEvenPercentage = requiredTurnover > 0 ? (branchOverallSales / requiredTurnover) * 100 : 0;
                      const isAboveBreakEven = breakEvenPercentage >= 100;
                      
                      const rankColor = idx === 0 ? 'bg-yellow-50 border-yellow-200' : 
                                       idx === 1 ? 'bg-gray-50 border-gray-200' : 
                                       idx === 2 ? 'bg-amber-50 border-amber-200' : 
                                       'bg-white';
                      
                      return (
                        <tr 
                          key={branch.code} 
                          id={`branch-${branch.code}`}
                          className={`${rankColor} hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                            idx === 0 ? 'border-yellow-400' : 
                            idx === 1 ? 'border-gray-400' : 
                            idx === 2 ? 'border-amber-400' : 
                            'border-transparent'
                          }`}
                          onClick={() => {
                            if (selectedBranchFilter === branch.code) {
                              handleClearBranchFilter();
                            } else {
                              setSelectedBranchFilter(branch.code);
                              setSearchParams({ branch: branch.code });
                            }
                          }}
                        >
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              {idx < 3 && (
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-3 ${
                                  idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                  idx === 1 ? 'bg-gray-400 text-gray-900' :
                                  'bg-amber-400 text-amber-900'
                                }`}>
                                  {idx + 1}
                                </span>
                              )}
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{branch.code}</div>
                                {selectedBranchFilter === branch.code && (
                                  <div className="text-xs text-blue-600">Currently filtered</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                            {formatCurrency(branchOverallSales)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {formatCurrency(branchTotalCost)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {formatCurrency(marginKpi?.value || 0)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              (branch.marginPercent || 0) >= 20 ? 'bg-green-100 text-green-800' :
                              (branch.marginPercent || 0) >= 15 ? 'bg-yellow-100 text-yellow-800' :
                              (branch.marginPercent || 0) >= 0 ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {formatPercentage(branch.marginPercent || 0)}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {isAboveBreakEven ? (
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              )}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                isAboveBreakEven 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {formatPercentage(breakEvenPercentage)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-700">
                            {formatCurrency(requiredTurnover)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {formatPercentage(salesPercentage)}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Individual Branch Performance - Collapsible Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Branch Performance</h2>
            <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded border border-blue-200">
              Current Month Totals
            </span>
          </div>
          <div className="space-y-4">
            {processedData.branches.map((branch, idx) => {
            const salesKpi = branch.kpis?.find(k => k.key.startsWith('sales-'));
            const costKpi = branch.kpis?.find(k => k.key.startsWith('cost-'));
            const marginKpi = branch.kpis?.find(k => k.key.startsWith('margin-'));
            const creditsKpi = branch.kpis?.find(k => k.key.startsWith('credits-'));
            const staffCostsKpi = branch.kpis?.find(k => k.key.startsWith('staff-costs-'));
            const requiredTurnoverKpi = branch.kpis?.find(k => k.key.startsWith('required-turnover-'));
            const profitLossKpi = branch.kpis?.find(k => k.key.startsWith('profit-loss-'));
            const isExpanded = expandedBranches.has(branch.code);
            
            // Get branch settings for targets
            const branchSetting = branchSettings?.find(b => b.MamCode === branch.code || b.Branch === branch.code);
            const dailyTarget = branchSetting?.DailySalesTarget;
            const monthlyTarget = branchSetting?.MonthlySalesTarget;
            
            return (
              <div 
                key={branch.code} 
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Header - Clickable to expand/collapse */}
                <div 
                  className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleBranch(branch.code)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg ${
                        idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                        idx === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                        idx === 2 ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                        'bg-gradient-to-br from-blue-400 to-blue-600'
                      }`}>
                        {branch.code.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{branch.code}</h3>
                        <p className="text-sm text-gray-500">Branch Performance</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{formatCurrency(salesKpi?.value || 0)}</div>
                      <div className="text-sm text-gray-500">Total Sales</div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                        <div className="text-xs font-medium text-blue-700 mb-1">Total Cost</div>
                        <div className="text-lg font-semibold text-blue-900">{formatCurrency(costKpi?.value || 0)}</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                        <div className="text-xs font-medium text-green-700 mb-1">Margin</div>
                        <div className="text-lg font-semibold text-green-900">{formatCurrency(marginKpi?.value || 0)}</div>
                      </div>
                      <div className={`rounded-lg p-3 border ${
                        (branch.marginPercent || 0) >= 0 ? 'bg-purple-50 border-purple-100' : 'bg-red-50 border-red-100'
                      }`}>
                        <div className={`text-xs font-medium mb-1 ${
                          (branch.marginPercent || 0) >= 0 ? 'text-purple-700' : 'text-red-700'
                        }`}>Margin %</div>
                        <div className={`text-lg font-semibold ${
                          (branch.marginPercent || 0) >= 0 ? 'text-purple-900' : 'text-red-900'
                        }`}>{formatPercentage(branch.marginPercent || 0)}</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                        <div className="text-xs font-medium text-orange-700 mb-1">Credits</div>
                        <div className="text-lg font-semibold text-orange-900">{formatCurrency(creditsKpi?.value || 0)}</div>
                      </div>
                    </div>
                    
                    {/* Profitability Metrics */}
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Profitability Analysis</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                          <div className="text-xs font-medium text-orange-700 mb-1">Required Turnover</div>
                          <div className="text-lg font-semibold text-orange-900">{formatCurrency(requiredTurnoverKpi?.value || 0)}</div>
                          {requiredTurnoverKpi?.subtitle && (
                            <div className="text-xs text-orange-600 mt-1">{requiredTurnoverKpi.subtitle}</div>
                          )}
                        </div>
                        <div className={`rounded-lg p-3 border ${
                          (profitLossKpi?.value || 0) >= 0 
                            ? 'bg-green-50 border-green-100' 
                            : 'bg-red-50 border-red-100'
                        }`}>
                          <div className={`text-xs font-medium mb-1 ${
                            (profitLossKpi?.value || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            Profit/Loss
                          </div>
                          <div className={`text-lg font-semibold ${
                            (profitLossKpi?.value || 0) >= 0 ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {formatCurrency(profitLossKpi?.value || 0)}
                          </div>
                          {profitLossKpi?.subtitle && (
                            <div className={`text-xs mt-1 ${
                              (profitLossKpi?.value || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {profitLossKpi.subtitle}
                            </div>
                          )}
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <div className="text-xs font-medium text-red-700 mb-1">Staff Costs (Monthly)</div>
                          <div className="text-lg font-semibold text-red-900">{formatCurrency(staffCostsKpi?.value || 0)}</div>
                          {staffCostsKpi?.subtitle && (
                            <div className="text-xs text-red-600 mt-1">{staffCostsKpi.subtitle}</div>
                          )}
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                          <div className="text-xs font-medium text-purple-700 mb-1">Avg Staff Member Cost</div>
                          <div className="text-lg font-semibold text-purple-900">
                            {formatCurrency(
                              staffCostsKpi?.subtitle 
                                ? (() => {
                                    const match = staffCostsKpi.subtitle.match(/(\d+)\sstaff/);
                                    const staffCount = match ? parseInt(match[1]) : 0;
                                    return staffCount > 0 ? (staffCostsKpi?.value || 0) / staffCount : 0;
                                  })()
                                : 0
                            )}
                          </div>
                          <div className="text-xs text-purple-600 mt-1">Per staff member</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Branch Targets */}
                    {(dailyTarget || monthlyTarget) && (
                      <div className="pt-4 border-t border-gray-200 mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Sales Targets</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {dailyTarget && (
                            <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-100">
                              <div className="text-xs font-medium text-cyan-700 mb-1">Daily Target</div>
                              <div className="text-lg font-semibold text-cyan-900">{formatCurrency(dailyTarget)}</div>
                            </div>
                          )}
                          {monthlyTarget && (
                            <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                              <div className="text-xs font-medium text-teal-700 mb-1">Monthly Target</div>
                              <div className="text-lg font-semibold text-teal-900">{formatCurrency(monthlyTarget)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex flex-wrap gap-3">
                        <a
                          href="/sales"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors inline-block text-center"
                        >
                          View Sales Report
                        </a>
                        <a
                          href="/settings/staff"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors inline-block text-center"
                        >
                          Staff Settings
                        </a>
                        <a
                          href="/settings/branch"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors inline-block text-center"
                        >
                          Branch Settings
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      </div>
    </ReportLayout>
  );
};

// Email Report Modal Component
const EmailReportModal = ({ onSend }) => {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);

  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValid(value === '' || validateEmail(value));
  };

  const handleSend = () => {
    if (!email.trim()) {
      setIsValid(false);
      return;
    }
    
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }

    onSend(email.trim());
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recipient Email Address *
        </label>
        <input
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="recipient@example.com"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            !isValid ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {!isValid && (
          <p className="mt-1 text-sm text-red-600">Please enter a valid email address</p>
        )}
      </div>
      
      <div className="flex justify-end space-x-3 mt-6">
        <button
          onClick={() => modals.closeAll()}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!email.trim() || !isValid}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send Report
        </button>
      </div>
    </div>
  );
};

export default BranchReport;
